import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useSpeedBackend } from '@/features/wallet';
import { hlInfo } from '@/lib/hyperliquidInfo';
import { queryKeys } from '@/lib/query';

/**
 * Detects the one agent failure the backend cannot see.
 *
 * Hyperliquid PRUNES an account's agent registration (and its builder-fee approval) when the
 * account is emptied completely. `GET /speed/agent/status` is a pure db read, so it keeps
 * answering `needsReactivation: false` and the user only finds out when a trade fails. Reading
 * the public `extraAgents` info endpoint for the user's adapter is the cheap way to notice; when
 * our agent is not in that list we tell the backend (`POST /speed/agent/report-pruned`), which
 * clears its bookkeeping so the ordinary Reactivate banner appears and the next reactivation
 * re-runs the builder approval too.
 */

const EXTRA_AGENTS_TIMEOUT_MS = 5_000;
const EXTRA_AGENTS_STALE_TIME_MS = 5 * 60_000;

/**
 * Agent approvals are issued with ~180 days of validity, so `validUntil - 180d` is effectively
 * the mint time. Used only for the grace window below.
 */
const AGENT_VALIDITY_MS = 180 * 24 * 60 * 60 * 1000;
/**
 * A just-approved agent takes a few seconds to show up in `extraAgents`, and reporting a prune in
 * that gap would undo a reactivation that actually worked. Skip detection entirely while the
 * approval looks freshly minted.
 */
const FRESH_MINT_GRACE_MS = 10 * 60_000;

export type AgentPruneVerdict =
    /** the agent is registered on Hyperliquid — nothing to do */
    | 'present'
    /** a SUCCESSFUL read positively lacks our agent: the account was pruned */
    | 'pruned'
    /** approval is too new for `extraAgents` to be trusted yet */
    | 'freshMint'
    /** nothing decidable: no agent to look for, or the read did not succeed */
    | 'unknown';

/**
 * The whole decision, as a pure function. `extraAgents: null` means the read did not succeed —
 * it must never be confused with "the read succeeded and the list is empty", because only the
 * latter is evidence of a prune.
 */
export function agentPrunedVerdict(input: {
    statusAgent: string | null;
    extraAgents: string[] | null;
    validUntil: number | null;
    nowMs: number;
}): AgentPruneVerdict {
    const { statusAgent, extraAgents, validUntil, nowMs } = input;
    if (!statusAgent || extraAgents === null) return 'unknown';

    const wanted = statusAgent.toLowerCase();
    if (extraAgents.some((agent) => agent.toLowerCase() === wanted)) {
        return 'present';
    }
    if (
        validUntil !== null &&
        validUntil > nowMs + AGENT_VALIDITY_MS - FRESH_MINT_GRACE_MS
    ) {
        return 'freshMint';
    }
    return 'pruned';
}

/** Agent addresses currently registered for `user`, from the public Hyperliquid info API. */
async function fetchExtraAgents(
    user: string,
    signal?: AbortSignal,
): Promise<string[]> {
    // the query's own signal alone would never time out — a hung request would keep the verdict
    // 'unknown' forever, so hlInfo combines the timeout in rather than replacing it.
    // accounts with no agents answer `null`, not `[]`
    const agents = await hlInfo<{ address?: string }[] | null | undefined>(
        { type: 'extraAgents', user },
        { signal, timeoutMs: EXTRA_AGENTS_TIMEOUT_MS },
    );
    return (agents ?? [])
        .map((agent) => agent?.address)
        .filter((address): address is string => typeof address === 'string');
}

/**
 * (adapter, agent) pairs already reported. Module level so a remount cannot re-fire the report,
 * and keyed by the pair so a NEW agent for the same adapter is still reportable later.
 */
const reportedPrunedAgents = new Set<string>();

/** test-only: clears the reported-prune record so a suite starts from a known state. */
export function resetReportedPrunedAgents(): void {
    reportedPrunedAgents.clear();
}

export interface UseAgentPruneDetectionInput {
    /** The user's HyperliquidAdapter address — the account whose agents are read. */
    adapter: string | null;
    /** Agent address the backend believes is approved. */
    agentAddress: string | null;
    validUntil: number | null;
    needsReactivation: boolean;
    isRealMode: boolean;
    isReactivating: boolean;
}

/**
 * Mount ONCE (Trading.tsx). Everything it needs comes in as arguments so it stays a single
 * instance — unlike `useAgentReactivation`, which is reached from ~8 places.
 */
export function useAgentPruneDetection(
    input: UseAgentPruneDetectionInput,
): void {
    const {
        adapter,
        agentAddress,
        validUntil,
        needsReactivation,
        isRealMode,
        isReactivating,
    } = input;
    const backend = useSpeedBackend();
    const queryClient = useQueryClient();

    // only worth asking when the backend claims everything is fine: if it already knows a
    // reactivation is due, or one is running, the banner is (or is about to be) up anyway.
    const enabled =
        isRealMode &&
        !isReactivating &&
        !needsReactivation &&
        !!agentAddress &&
        !!adapter;

    const { data: extraAgents = null } = useQuery({
        queryKey: ['speed', 'agent', 'extraAgents', adapter],
        queryFn: async ({ signal }) => fetchExtraAgents(adapter!, signal),
        enabled,
        staleTime: EXTRA_AGENTS_STALE_TIME_MS,
        // a public read; refreshing it when the user comes back is the cheapest way to catch a
        // prune that happened while the tab was in the background.
        refetchOnWindowFocus: true,
        retry: 1,
    });

    const verdict = agentPrunedVerdict({
        statusAgent: enabled ? agentAddress : null,
        extraAgents,
        validUntil,
        nowMs: Date.now(),
    });

    useEffect(() => {
        if (verdict !== 'pruned' || !backend || !adapter || !agentAddress) {
            return;
        }
        const key = `${adapter.toLowerCase()}:${agentAddress.toLowerCase()}`;
        if (reportedPrunedAgents.has(key)) return;
        // marked BEFORE the request: the report is idempotent server-side, and a retry loop here
        // would be worse than a signal lost to a transient failure (the order-time
        // AGENT_REACTIVATION_REQUIRED still catches that case).
        reportedPrunedAgents.add(key);
        console.warn(
            `[AgentPrune] agent ${agentAddress} is no longer registered for adapter ${adapter} ` +
                '— Hyperliquid pruned it (account emptied). Reporting to the backend.',
        );
        backend
            .reportAgentPruned('agentMissing')
            .then(async () =>
                queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.agentStatus(),
                }),
            )
            .catch((error: unknown) => {
                console.warn('[AgentPrune] report-pruned failed:', error);
            });
    }, [verdict, backend, adapter, agentAddress, queryClient]);
}
